import { useState, useEffect } from 'react';
import {
  Loader2,
  Wrench,
  Archive,
  Plus,
  ChevronRight,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useRemontByClient } from '@/features/legacy/hooks/useRemont';
import { RemontDetailSheet } from './RemontDetailSheet';
import { RemontSheet } from '@/features/legacy/components/RemontSheet';
import { LinkText } from '@/components/LinkText';
import {
  serviceCenterRepairsApi,
  type RemontEntry,
} from '@/api/service-center-repairs';

interface ClientRemontSheetProps {
  clientId: number;
  clientName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function usePhotoUrl(photo: string | undefined) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!photo) return;

    let cancelled = false;
    let objectUrl: string | null = null;

    async function fetchImage() {
      try {
        const nextUrl = await serviceCenterRepairsApi.getPhotoBlobUrl(photo!);
        if (!cancelled) {
          objectUrl = nextUrl;
          setUrl(objectUrl);
        }
      } catch {
        // Ignore errors
      }
    }

    fetchImage();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [photo]);

  return photo ? url : null;
}

function StatusLine({ label, value, isYes }: { label: string; value: React.ReactNode; isYes?: boolean }) {
  return (
    <span className="flex gap-1">
      <span className="text-muted-foreground">{label}:</span>
      <span className={isYes ? '' : 'text-red-500'}>{value}</span>
    </span>
  );
}

function RemontCard({ entry, onClick }: { entry: RemontEntry; onClick: () => void }) {
  const photoUrl = usePhotoUrl(entry.photo);
  const costNum = parseFloat(entry.cost) || 0;
  const otvez = Number(entry.otvez) === 1;
  const zabral = Number(entry.zabral) === 1;
  const vistavleno = Number(entry.vistavlenoklientu) === 1;
  const oplacheno = Number(entry.oplacheno) === 1;
  const hasAnswer = !!entry.answer?.trim();

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-lg border overflow-hidden p-3 text-left transition-colors hover:bg-accent active:bg-accent ${
        entry.attentionLevel === 'RED'
          ? 'border-red-400 bg-red-50'
          : entry.attentionLevel === 'YELLOW'
            ? 'border-amber-400 bg-amber-50'
            : 'border-border bg-card'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-sm truncate">{entry.name}</h3>
          <p className="text-xs text-muted-foreground truncate mt-0.5">
            {entry.whose && `${entry.whose} • `}
            {entry.remont_place && `→ ${entry.remont_place}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {photoUrl && (
            <img
              src={photoUrl}
              alt=""
              className="w-12 h-12 rounded object-cover flex-shrink-0"
            />
          )}
          <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        </div>
      </div>

      <div className="mt-2 text-xs space-y-0.5">
        {entry.comment && (
          <p className="text-muted-foreground line-clamp-1">
            Жалоба: <LinkText>{entry.comment}</LinkText>
          </p>
        )}
        <div className="flex flex-wrap gap-x-3 gap-y-0.5">
          <StatusLine label="Отвёз" value={otvez ? 'Да' : 'Нет'} isYes={otvez} />
          <StatusLine label="Забрал" value={zabral ? 'Да' : 'Нет'} isYes={zabral} />
          <StatusLine label="Цена" value={costNum > 0 ? `${costNum} руб.` : 'Нет'} isYes={costNum > 0} />
          {hasAnswer && <StatusLine label="Ответ" value={<LinkText>{entry.answer!}</LinkText>} isYes />}
          <StatusLine label="Выставлено" value={vistavleno ? 'Да' : 'Нет'} isYes={vistavleno} />
          <StatusLine label="Оплата" value={oplacheno ? 'Да' : 'Нет'} isYes={oplacheno} />
        </div>
        {entry.attentionLevel !== 'NONE' && (
          <p className={entry.attentionLevel === 'RED' ? 'mt-1 text-red-700' : 'mt-1 text-amber-700'}>
            Без движения: {entry.inactivityDays} дн.
          </p>
        )}
        {entry.diagnostics.length > 0 && (
          <p className="mt-1 text-red-700">
            {entry.diagnostics.map((item) => item.message).join(' · ')}
          </p>
        )}
      </div>
    </button>
  );
}

export function ClientRemontSheet({
  clientId,
  clientName,
  open,
  onOpenChange,
}: ClientRemontSheetProps) {
  const [showArchive, setShowArchive] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<RemontEntry | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const { data: activeData, isLoading: activeLoading } = useRemontByClient(
    open ? clientId : undefined,
    false,
  );
  const { data: archiveData, isLoading: archiveLoading } = useRemontByClient(
    open && showArchive ? clientId : undefined,
    true,
  );

  const activeEntries = activeData?.entries || [];
  const archivedEntries = archiveData?.entries || [];
  const vendors = activeData?.vendors || archiveData?.vendors || [];

  return (
    <>
      <Sheet open={open && !selectedEntry && !createOpen} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
          <SheetHeader className="text-left">
            <SheetTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              Ремонт в СЦ
            </SheetTitle>
          </SheetHeader>

          <div className="mt-3 space-y-3">
            {/* Controls */}
            <div className="flex gap-2">
              <Button
                variant={showArchive ? 'secondary' : 'outline'}
                size="sm"
                onClick={() => setShowArchive(!showArchive)}
              >
                <Archive className="h-4 w-4 mr-1" />
                Архив
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCreateOpen(true)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Новая
              </Button>
            </div>

            {/* Active entries */}
            {activeLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : activeEntries.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground">
                <p className="text-sm">Нет активных заявок</p>
              </div>
            ) : (
              <div className="space-y-2">
                {activeEntries.map((entry) => (
                  <RemontCard
                    key={entry.id}
                    entry={entry}
                    onClick={() => setSelectedEntry(entry)}
                  />
                ))}
              </div>
            )}

            {/* Archive section */}
            {showArchive && (
              <>
                <div className="flex items-center gap-2 pt-2">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Archive className="h-3 w-3" />
                    Архив ({archivedEntries.length})
                  </span>
                  <div className="flex-1 h-px bg-border" />
                </div>

                {archiveLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : archivedEntries.length === 0 ? (
                  <div className="text-center py-4 text-muted-foreground">
                    <p className="text-sm">Нет архивных заявок</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {archivedEntries.map((entry) => (
                      <RemontCard
                        key={entry.id}
                        entry={entry}
                        onClick={() => setSelectedEntry(entry)}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Detail view */}
      <RemontDetailSheet
        entry={selectedEntry}
        vendors={vendors}
        open={!!selectedEntry}
        onOpenChange={(isOpen) => {
          if (!isOpen) setSelectedEntry(null);
        }}
      />

      {/* Create new */}
      <RemontSheet
        key={`create-${clientId}-${createOpen}`}
        entry={{ clientId, company: clientName }}
        vendors={vendors}
        open={createOpen}
        onOpenChange={(isOpen) => {
          setCreateOpen(isOpen);
        }}
      />
    </>
  );
}
